// Plays a Web Audio node through a local RTCPeerConnection pair, so the browser's echo
// canceller sees the customer's voice as call audio and subtracts it from the mic.
//
// Why: the mic asks for echoCancellation, but AEC only cancels what it has as a far-end
// reference. Chrome (incl. Android) builds that reference from WebRTC playout, not from Web
// Audio output, so the customer's voice played via AudioContext.destination can come straight
// back in as "trainee speech" (crbug.com/687574). Routing it through a loopback peer
// connection is the standard workaround. iOS cancels against all device output already, and
// its media-element autoplay rules make the extra hop riskier, so iOS keeps direct playout.
//
// ponytail: Opus at WebRTC defaults (~32 kbps mono) — fine for a voice; raise the bitrate via
// SDP if anyone hears the difference.

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// On by default since 2026-10-20, after a speaker-phone comparison on Android preferred it
// to direct output. `?aec=direct` forces it off; `?aec=loopback` forces it on.
const LOOPBACK_DEFAULT = true;
export const loopbackEnabled = () => {
  if (typeof RTCPeerConnection === 'undefined' || isIOS()) return false;
  const aec = new URLSearchParams(window.location.search).get('aec');
  return aec === 'loopback' || (LOOPBACK_DEFAULT && aec !== 'direct');
};

/** Resolves to a cleanup function; throws (after cleaning up) if playout can't start. */
export async function playViaLoopback(ctx, node, timeoutMs = 3000) {
  const dest = ctx.createMediaStreamDestination();
  node.connect(dest);
  const a = new RTCPeerConnection();
  const b = new RTCPeerConnection();
  const el = new Audio();
  el.autoplay = true;
  const cleanup = () => {
    try { node.disconnect(dest); } catch (e) { /* already disconnected */ }
    a.close();
    b.close();
    el.pause();
    el.srcObject = null;
  };
  try {
    a.onicecandidate = (e) => e.candidate && b.addIceCandidate(e.candidate).catch(() => {});
    b.onicecandidate = (e) => e.candidate && a.addIceCandidate(e.candidate).catch(() => {});
    const gotTrack = new Promise((resolve) => {
      b.ontrack = (e) => {
        el.srcObject = e.streams[0] || new MediaStream([e.track]);
        resolve();
      };
    });
    dest.stream.getTracks().forEach((t) => a.addTrack(t, dest.stream));
    const offer = await a.createOffer();
    await a.setLocalDescription(offer);
    await b.setRemoteDescription(offer);
    const answer = await b.createAnswer();
    await b.setLocalDescription(answer);
    await a.setRemoteDescription(answer);
    await Promise.race([
      gotTrack,
      new Promise((_, reject) => setTimeout(() => reject(new Error('loopback: no track')), timeoutMs)),
    ]);
    await el.play();
    return cleanup;
  } catch (err) {
    cleanup();
    throw err;
  }
}
